import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { scrollToSection } from "@/components/landing/Nav";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  const browseShop = () => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection("shop"), 600);
    } else {
      scrollToSection("shop");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l border-gold/20 bg-ink-card text-foreground sm:max-w-md"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2.5 font-display text-xl text-foreground">
            <ShoppingBag className="h-5 w-5 text-gold" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <ShoppingBag className="h-12 w-12 text-gold/30" />
            <p className="text-sm text-muted-foreground">Your cart is empty.</p>
            <Button data-testid="cart-browse-button" onClick={browseShop} variant="outline"
              className="rounded-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
              Browse the Shop
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-6 flex-1 space-y-4 overflow-y-auto pr-1">
              {items.map((item, i) => (
                <div key={i} data-testid={`cart-item-${i}`} className="flex gap-4 rounded-xl border border-gold/15 bg-ink p-3.5">
                  <img src={item.image || "/assets/logo.png"} alt={item.name}
                    className="h-20 w-20 shrink-0 rounded-lg border border-gold/15 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{item.name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.type === "event"
                        ? `Event · ${item.quantity} attendee${item.quantity > 1 ? "s" : ""}`
                        : [item.variation_name, item.size ? `Size ${item.size}` : null].filter(Boolean).join(" · ") || "Item"}
                    </p>
                    <div className="mt-2.5 flex items-center justify-between">
                      {item.type === "product" ? (
                        <div className="flex items-center gap-2">
                          <button data-testid={`cart-qty-minus-${i}`} aria-label="Decrease quantity"
                            onClick={() => setQty(i, item.quantity - 1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/25 text-gold hover:bg-gold/10">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-5 text-center text-sm" data-testid={`cart-qty-${i}`}>{item.quantity}</span>
                          <button data-testid={`cart-qty-plus-${i}`} aria-label="Increase quantity"
                            onClick={() => setQty(i, item.quantity + 1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/25 text-gold hover:bg-gold/10">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      ) : <span />}
                      <span className="font-display text-sm font-semibold text-gold">
                        ${(item.unit_price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${i}`} aria-label="Remove item" onClick={() => remove(i)}
                    className="self-start text-muted-foreground transition-colors hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-gold/15 pt-5">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-xl font-semibold text-gold">${subtotal.toFixed(2)}</span>
              </div>
              <Button data-testid="cart-checkout-button" onClick={goCheckout}
                className="mt-4 w-full rounded-full bg-gold py-6 font-semibold text-ink transition-all hover:bg-gold-hover">
                Proceed to Checkout
              </Button>
              <button data-testid="cart-continue-shopping" onClick={browseShop}
                className="mt-3 w-full text-center text-xs text-muted-foreground transition-colors hover:text-gold">
                Continue shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
